import { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useRevocarTema } from "../../queries/useEstudiantes";
import { mensajeDeError } from "../../utils/errores";

/**
 * Confirmación para revocarle el tema a un estudiante.
 *
 * Se confirma antes de hacer nada porque la acción se sale del proceso donde se
 * aprieta: el tema vuelve al catálogo y queda al alcance de cualquiera, y el
 * estudiante pierde también a su guía en todos sus procesos en curso. Eso último no
 * se ve desde el botón, así que se dice acá.
 *
 * El motivo es obligatorio: es el cuerpo del correo que recibe el estudiante y lo
 * que queda escrito en su ficha. Sin él, dentro de un mes nadie recuerda por qué.
 */
export default function RevocarTemaDialog({
  rut,
  estudiante,
  tema,
  onClose,
  onRevocado,
}: {
  rut: number;
  estudiante: string;
  tema: string;
  onClose: () => void;
  /** Además de cerrar: quien lo abrió desde otro diálogo cierra también el suyo. */
  onRevocado?: () => void;
}) {
  const revocar = useRevocarTema(rut);
  const [motivo, setMotivo] = useState("");

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Revocar el tema de {estudiante}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            «{tema}» <strong>vuelve al catálogo como disponible</strong> y cualquier otro estudiante va a poder postular a él.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sus procesos en curso quedan sin tema y sin guía. Los que ya están cerrados no se tocan: el tema sigue ahí como
            parte de su historial.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Después podrá postular a otro tema, y ese se enganchará a sus procesos en curso.
          </Typography>
          <TextField
            label="Motivo"
            required
            autoFocus
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            placeholder="Se le explica al estudiante por correo y queda en su ficha."
          />
          {revocar.isError && <Alert severity="error">{mensajeDeError(revocar.error, "No se pudo revocar el tema.")}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={revocar.isPending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={revocar.isPending || !motivo.trim()}
          onClick={() => revocar.mutate(motivo.trim(), {
              onSuccess: () => {
                onClose();
                onRevocado?.();
              },
            })}
        >
          {revocar.isPending ? "Revocando…" : "Revocar tema"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
